import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Edit2 } from 'lucide-react';
import CategoryAccordion from './CategoryAccordion';
import { formatIndicatorDisplay, mergeCustomIndicators } from '../../utils/indicatorCategories';
import { useCustomIndicators } from '../../contexts/CustomIndicatorContext';

/**
 * The "Indicators N" dropdown that sits above an AdvancedPriceChartWidget.
 *
 * This markup was previously inlined in PriceChartModal, OptionsPremiumChartModal
 * and WatchlistContractCard. It is extracted here so new chart surfaces stop
 * adding copies; the three existing surfaces still carry their own inline
 * versions and can adopt this in a separate pass.
 *
 * The caller keeps ownership of `indicators` / `indicatorSettings` (each surface
 * persists them under its own localStorage keys). This component owns only its
 * open/hover state and resolves custom indicators down to their base indicator
 * before reporting a toggle.
 */
export default function IndicatorPickerDropdown({
  indicators = [],
  indicatorSettings = {},
  onToggleIndicator,       // (baseIndicator: string, customSettings: object|null) => void
  onEditIndicator,         // (indicator: string) => void
  showBollinger = false,
  onShowBollingerChange,   // (bool) => void
  align = 'right',         // 'right' | 'left'
  maxHeightClass = 'max-h-80',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndicator, setHoveredIndicator] = useState(null);
  const dropdownRef = useRef(null);

  const { customIndicators } = useCustomIndicators();
  const mergedCategories = mergeCustomIndicators(customIndicators);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // A "custom_<id>" entry is a saved preset over a base indicator — report the
  // base indicator plus the preset's settings so the caller stores both.
  const handleToggle = (indicatorValue) => {
    if (!onToggleIndicator) return;
    if (!indicatorValue.startsWith('custom_')) {
      onToggleIndicator(indicatorValue, null);
      return;
    }
    const customId = parseInt(indicatorValue.replace('custom_', ''), 10);
    const customIndicator = customIndicators.find(ci => ci.id === customId);
    if (customIndicator) {
      onToggleIndicator(customIndicator.base_indicator, customIndicator.settings || null);
    } else {
      onToggleIndicator(indicatorValue, null);
    }
  };

  const handleEdit = (indicator) => {
    setIsOpen(false);
    if (onEditIndicator) onEditIndicator(indicator);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
        title="Indicators"
      >
        <span>Indicators</span>
        {indicators.length > 0 && (
          <span className="px-1.5 py-0.5 bg-blue-600/30 text-blue-400 rounded text-[10px]">
            {indicators.length}
          </span>
        )}
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 ${maxHeightClass} overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Active Indicators */}
          <div className="px-3 py-2 border-b border-gray-700">
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
            <div className="space-y-1">
              {indicators.length > 0 ? (
                indicators.map(ind => (
                  <div
                    key={ind}
                    className="flex items-center justify-between text-sm text-gray-200 px-2 py-1 rounded hover:bg-gray-700/50 group"
                    onMouseEnter={() => setHoveredIndicator(ind)}
                    onMouseLeave={() => setHoveredIndicator(null)}
                  >
                    <button
                      type="button"
                      onClick={() => handleEdit(ind)}
                      className="flex-1 text-left hover:text-blue-400 transition-colors"
                      title="Click to edit settings"
                    >
                      {formatIndicatorDisplay(ind, indicatorSettings[ind])}
                    </button>
                    <div className="flex items-center gap-1">
                      {hoveredIndicator === ind && (
                        <button
                          type="button"
                          onClick={() => handleEdit(ind)}
                          className="text-gray-400 hover:text-white transition-colors px-1"
                          title="Edit settings"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleToggle(ind)}
                        className="text-gray-400 hover:text-red-400 text-xs"
                        title="Remove indicator"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-500">None</span>
              )}
            </div>
          </div>

          {/* Bollinger Toggle */}
          {indicators.includes('bollinger') && onShowBollingerChange && (
            <div className="px-3 py-2 border-b border-gray-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showBollinger}
                  onChange={(e) => onShowBollingerChange(e.target.checked)}
                  className="w-3 h-3"
                />
                <span className="text-xs text-gray-300">Show Bollinger Bands</span>
              </label>
            </div>
          )}

          {/* Available Indicators */}
          <div className="px-3 py-2">
            <p className="text-xs text-gray-400 mb-2">Add Indicators:</p>
            <CategoryAccordion
              categories={mergedCategories}
              selectedItems={indicators}
              onToggle={handleToggle}
              onEdit={handleEdit}
              displayMode="compact"
            />
          </div>
        </div>
      )}
    </div>
  );
}
